import { ROUTES } from '../utils/constants/routes';

const isGovtIdAttemptsExhausted = ({
    workflowIdVerifications,
    verificationDetails,
    documentVerified,
    isLivenessCheckEnabled,
}) => {
    const govtIdAttemptsCount =
        verificationDetails?.govt_id_attempts >= workflowIdVerifications?.govt_id?.attempts;

    // If selfie is not enabled, only check govt_id attempts
    if (!isLivenessCheckEnabled) {
        return govtIdAttemptsCount;
    }

    // If selfie is enabled, check both govt_id and selfie attempts
    if (!documentVerified) {
        // If govt_id is not valid, count as attempts exhausted
        return govtIdAttemptsCount;
    }

    // If govt_id is valid but attempts are 0, check selfie attempts
    if (govtIdAttemptsCount) {
        // Only consider exhausted if selfie attempts are also exceeded
        return (
            verificationDetails?.selfie_attempts >=
            workflowIdVerifications?.govt_id?.metadata?.livenessCheck?.attempts
        );
    }

    return false;
};

const checkAndFlowIncompleteWithExhaustedAttempts = (
    enabledMethods,
    methodToStatusMap,
    verificationStatus,
    attemptsExhausted
) => {
    const hasUnverifiedMethodWithExhaustedAttempts = enabledMethods.some((method) => {
        const statusKey = methodToStatusMap[method];

        return !verificationStatus[statusKey] && attemptsExhausted[method];
    });

    const allMethodsCompletedOrExhausted = enabledMethods.every((method) => {
        const statusKey = methodToStatusMap[method];
        return verificationStatus[statusKey] || attemptsExhausted[method];
    });

    return hasUnverifiedMethodWithExhaustedAttempts || allMethodsCompletedOrExhausted;
};

const checkOrFlowIncompleteWithExhaustedAttempts = (
    enabledMethods,
    methodToStatusMap,
    verificationStatus,
    attemptsExhausted
) => {
    const allMethodsExhausted = enabledMethods.every((method) => attemptsExhausted[method]);
    const noMethodsVerified = enabledMethods.every((method) => {
        const statusKey = methodToStatusMap[method];
        return !verificationStatus[statusKey];
    });

    return allMethodsExhausted && noMethodsVerified;
};

export const isVerificationCompleted = ({
    extendedInputs = {},
    kbaVerified,
    bankVerified,
    documentVerified,
    selfieAttempted,
    workflowIdVerifications = {},
    verificationDetails = {},
    verificationConfiguration,
    quickVerifyPassed,
}) => {
    const isEEPEnabled = Object.values(extendedInputs).find((input) => input === true) ?? false;

    // Setup verification configuration
    const { verification_option: ignoreVerificationOption, ...idVerificationMethods } =
        workflowIdVerifications;
    delete idVerificationMethods?.quick_verify;
    const enabledMethods = Object.keys(idVerificationMethods)?.filter(
        (method) => idVerificationMethods[method]?.enabled
    );
    // Determine flow type (AND/OR) based on verificationConfiguration
    const isAndFlow = verificationConfiguration === 'AND' || verificationConfiguration === 'SINGLE';

    // Setup verification status
    const isLivenessCheckEnabled =
        workflowIdVerifications?.govt_id?.metadata?.livenessCheck?.enabled;
    const isDocumentFullyVerified = isLivenessCheckEnabled
        ? documentVerified && selfieAttempted
        : documentVerified;

    const verificationStatus = {
        kbaVerified,
        bankVerified,
        documentVerified: isDocumentFullyVerified,
    };

    // Setup method mappings
    const methodToStatusMap = {
        kba: 'kbaVerified',
        govt_id: 'documentVerified',
        bank: 'bankVerified',
    };

    // Check attempts exhaustion for each method
    // For govt_id: if liveness check is enabled, both govt_id and selfie attempts must be exhausted
    // For other methods: only their own attempts need to be exhausted
    const attemptsExhausted = {
        kba:
            workflowIdVerifications?.kba?.enabled &&
            verificationDetails?.kba_attempts >= workflowIdVerifications?.kba?.attempts,
        govt_id:
            workflowIdVerifications?.govt_id?.enabled &&
            isGovtIdAttemptsExhausted({
                workflowIdVerifications,
                verificationDetails,
                documentVerified,
                isLivenessCheckEnabled,
            }),
        bank:
            workflowIdVerifications?.bank?.enabled &&
            verificationDetails?.bank_attempts >= workflowIdVerifications?.bank?.attempts,
    };

    // Determine if each method is completed (verified OR attempts exhausted)
    const methodCompletion = {
        kba: verificationStatus.kbaVerified,
        govt_id: verificationStatus.documentVerified,
        bank: verificationStatus.bankVerified,
    };

    // Check overall verification completion
    const isVerificationCompletedResult = isAndFlow
        ? enabledMethods.every((method) => methodCompletion[method]) // AND flow: ALL methods must be completed (verified OR attempts exhausted)
        : enabledMethods.some((method) => verificationStatus[methodToStatusMap[method]]); // OR flow: At least ONE method must be verified

    // Check if verification is incomplete but attempts are exhausted
    const isIncompleteWithExhaustedAttempts = isAndFlow
        ? checkAndFlowIncompleteWithExhaustedAttempts(
              enabledMethods,
              methodToStatusMap,
              verificationStatus,
              attemptsExhausted
          )
        : checkOrFlowIncompleteWithExhaustedAttempts(
              enabledMethods,
              methodToStatusMap,
              verificationStatus,
              attemptsExhausted
          );

    return {
        isIdentityVerificationCompleted: isVerificationCompletedResult || quickVerifyPassed,
        isEEPEnabled,
        isIncompleteWithExhaustedAttempts,
    };
};

/**
 * Convert an absolute `ROUTES` path into a segment relative to a mount base, for
 * use as a descendant `<Route path>`. A descendant `<Routes>` matches against the
 * pathname remaining after its parent's base, so nested definitions must be
 * relative — deriving them from the absolute constant keeps `ROUTES` the single
 * source of truth. Returns '' for the base itself (mount it as an index route).
 * @param {string} base - The absolute path the child tree is mounted under.
 * @param {string} path - The absolute `ROUTES` path to make relative.
 * @returns {string} The path relative to `base`, without a leading slash.
 */
export const relativeRoute = (base, path) => {
    const relative = path.startsWith(base) ? path.slice(base.length) : path;
    return relative.replace(/^\//, '');
};

// get the list of extended inputs that has been enabled in Array
export const getExtendedInputsArray = (obj) => {
    const extendedChecks = [];
    for (const key in obj) {
        if (obj.hasOwnProperty(key) && obj[key] === true) {
            extendedChecks.push(key);
        }
    }
    return extendedChecks;
};

export const updateDefaultRoute = (payload) => {
    const defaultRoute = [ROUTES.ROOT, ROUTES.VERIFICATION, ROUTES.THANK_YOU];

    const preIdentityChecks = payload?.workflow?.preIdentityChecks;

    const verificationDetails = payload?.company?.user?.verificationDetails;

    const twoFaEnabled = preIdentityChecks?.['2Fa']?.enabled;

    if (twoFaEnabled) {
        defaultRoute.splice(1, 0, ROUTES.TWO_FACTOR_AUTHENTICATION);
    }

    if (
        preIdentityChecks?.riskScore?.enabled &&
        verificationDetails?.isRiskScoreVerified === null
    ) {
        const index = twoFaEnabled ? 2 : 1;

        defaultRoute.splice(index, 0, ROUTES.RISK_ASSESSMENT);
    }

    if (payload?.company?.user?.extendedInputs) {
        if (payload.company.user.extendedInputs.education) {
            defaultRoute.splice(defaultRoute.length - 1, 0, ROUTES.EDUCATION);
        }

        if (payload.company.user.extendedInputs.employment) {
            defaultRoute.splice(defaultRoute.length - 1, 0, ROUTES.EMPLOYMENT);
        }

        if (payload.company.user.extendedInputs.professionalLicense) {
            defaultRoute.splice(defaultRoute.length - 1, 0, ROUTES.PROFESSIONAL_LICENSE);
        }
    }

    return defaultRoute;
};

export const shouldShowStepper = (profile, location) => {
    const isTwoFaEnabled = profile?.workflow?.pre_identity_checks?.['2fa']?.enabled;

    const isSupportScreen = location.pathname.split('/').includes('need-support');

    const ignoreRoutes = new Set(['/']).has(location.pathname);

    return (!ignoreRoutes && !isSupportScreen && isTwoFaEnabled) ?? false;
};

/**
 * Get the next route in the defaultRoute array based on the currentRoute.
 * @param {Object} params - The parameters object.
 * @param {Array} params.routes - An array of all available routes.
 * @param {string} params.currentRoute - The current route to find the next route for.
 * @returns {string|null} - The next route or null if not found.
 */
export const getNextRoute = ({ routes = [], currentRoute = '' }) => {
    const currentRouteIndex = routes?.findIndex((route) => route === currentRoute);

    return currentRouteIndex !== -1 ? routes[currentRouteIndex + 1] : currentRoute;
};
